import { useEffect, useMemo, useRef } from 'react';
import { IoAddOutline } from 'react-icons/io5';
import { Button } from '@/components/atoms/Button';
import { DatePicker, DatePickerRange } from '@/components/atoms/DatePicker';
import SimpleSelect from '@/components/atoms/SimpleSelect';
import TooltipIconText from '@/components/atoms/TooltipIconText';
import { formatDate, removeTimeZone } from '@/utils/formatDate';
import { getDateRange } from '@/utils/getDateRange';
import TrashButton from '../atoms/TrashButton';

export interface DateInterval {
	id: string;
	dateRange: DatePickerRange | undefined;
	entryTime: string;
	exitTime: string;
	location: string | number | null;
	confinement: boolean;
	needTransport: boolean;
}

export interface AvailablePeriod {
	startDate: string;
	endDate: string;
}

export const booleanToNumber = (value: boolean): number => {
	return value ? 1 : 0;
};

export const numberToBoolean = (value: number | string): boolean => {
	return Number(value) === 1;
};

function buildAvailableDaysSet(periods: AvailablePeriod[]): Set<string> {
	const set = new Set<string>();
	for (const period of periods) {
		const periodDays = getDateRange(
			removeTimeZone(new Date(period.startDate)),
			removeTimeZone(new Date(period.endDate)),
		);
		for (const day of periodDays) {
			set.add(formatDate(day));
		}
	}
	return set;
}

function isDateRangeFullyWithinAvailableDays(
	dateRange: DatePickerRange,
	availableDaysSet: Set<string>,
): boolean {
	if (!dateRange.startDate || !dateRange.endDate) {
		return true;
	}

	const start = removeTimeZone(new Date(dateRange.startDate));
	const end = removeTimeZone(new Date(dateRange.endDate));
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
		return false;
	}

	if (availableDaysSet.size === 0) {
		return false;
	}

	const days = getDateRange(start, end);
	if (days.length === 0) {
		return false;
	}

	return days.every((day) => availableDaysSet.has(formatDate(day)));
}

interface DateIntervalsSectionProps {
	dateIntervals: DateInterval[];
	yesNoOptions: Array<{ value: number; label: string }>;
	transportTypeOptions: Array<{ value: string; label: string }>;
	locations: Array<{ value: number; label: string }>;
	availableDatesPeriods: AvailablePeriod[] | null;
	isAvailableDatesReady: boolean;
	onAddDateInterval: () => void;
	onRemoveDateInterval: (id: string) => void;
	onUpdateDateInterval: (
		id: string,
		field: keyof DateInterval,
		value: string | number | boolean | DatePickerRange | null | undefined,
	) => void;
	isTransportNeeded?: boolean;
	isIntervalTransportLocked?: (interval: DateInterval) => boolean;
	lockNeedTransportMessage?: string | null;
}

const DateIntervalsSection: React.FC<DateIntervalsSectionProps> = ({
	dateIntervals,
	yesNoOptions,
	locations,
	availableDatesPeriods,
	isAvailableDatesReady,
	onAddDateInterval,
	onRemoveDateInterval,
	onUpdateDateInterval,
	isTransportNeeded = false,
	isIntervalTransportLocked,
	lockNeedTransportMessage = null,
}) => {
	const dateIntervalsRef = useRef(dateIntervals);
	const onUpdateDateIntervalRef = useRef(onUpdateDateInterval);
	dateIntervalsRef.current = dateIntervals;
	onUpdateDateIntervalRef.current = onUpdateDateInterval;

	const rawMinDate = availableDatesPeriods?.at(0)?.startDate;
	const rawMaxDate = availableDatesPeriods?.at(-1)?.endDate;

	const minDate = rawMinDate
		? removeTimeZone(new Date(rawMinDate))
		: removeTimeZone(new Date());
	const maxDate = rawMaxDate
		? removeTimeZone(new Date(rawMaxDate))
		: removeTimeZone(new Date());

	// biome-ignore lint/correctness/useExhaustiveDependencies(minDate): derived from availableDatesPeriods
	// biome-ignore lint/correctness/useExhaustiveDependencies(maxDate): derived from availableDatesPeriods
	const disabledPeriods = useMemo((): Array<{
		startDate: Date;
		endDate: Date;
	}> => {
		if (!availableDatesPeriods) {
			return [];
		}

		if (availableDatesPeriods.length === 0) {
			return [
				{
					startDate: minDate,
					endDate: maxDate,
				},
			];
		}

		const availableDaysSet = buildAvailableDaysSet(availableDatesPeriods);

		const fullPeriod = getDateRange(minDate, maxDate);
		const disabledPeriodsResult: Array<{
			startDate: Date;
			endDate: Date;
		}> = [];
		let currentDisabledStart: Date | undefined;

		for (const currentDate of fullPeriod) {
			const dateKey = formatDate(currentDate);
			const isOffShiftDay = availableDaysSet.has(dateKey);

			if (!isOffShiftDay) {
				if (!currentDisabledStart) {
					currentDisabledStart = currentDate;
				}
			} else {
				if (currentDisabledStart) {
					const endDate = new Date(currentDate);
					endDate.setDate(endDate.getDate() - 1);

					if (endDate >= currentDisabledStart) {
						disabledPeriodsResult.push({
							startDate: currentDisabledStart,
							endDate: endDate,
						});
					}
					currentDisabledStart = undefined;
				}
			}
		}

		if (currentDisabledStart) {
			disabledPeriodsResult.push({
				startDate: currentDisabledStart,
				endDate: maxDate,
			});
		}

		return disabledPeriodsResult;
	}, [availableDatesPeriods]);

	useEffect(() => {
		if (!isAvailableDatesReady || !availableDatesPeriods) {
			return;
		}

		const availableDaysSet = buildAvailableDaysSet(availableDatesPeriods);
		const onUpdate = onUpdateDateIntervalRef.current;

		for (const interval of dateIntervalsRef.current) {
			const range = interval.dateRange;
			if (!range?.startDate || !range?.endDate) {
				continue;
			}
			if (isDateRangeFullyWithinAvailableDays(range, availableDaysSet)) {
				continue;
			}
			onUpdate(interval.id, 'dateRange', undefined);
			onUpdate(interval.id, 'entryTime', '');
			onUpdate(interval.id, 'exitTime', '');
		}
	}, [availableDatesPeriods, isAvailableDatesReady]);

	return (
		<div className="mt-4 rounded-md border border-[#CBD5E1] p-4">
			<div className="space-y-4">
				{dateIntervals.map((interval, index) => {
					const isLastInterval = index === dateIntervals.length - 1;
					const lockNeedTransport =
						isIntervalTransportLocked?.(interval) ?? false;
					return (
						<div key={interval.id} className="flex flex-col gap-4">
							<div className="flex items-end gap-4">
								<div className="grid grid-cols-5 gap-4 items-end flex-1">
									<div className="w-full">
										<div className="mb-1 text-[12px] text-[#505050] font-normal">
											Data da ida extra
											<span className="text-red">*</span>
										</div>
										<DatePicker
											value={interval.dateRange}
											onSelect={(dateRange) =>
												onUpdateDateInterval(
													interval.id,
													'dateRange',
													dateRange,
												)
											}
											className={{
												button:
													'h-8 w-full rounded-[4px] border border-gray-400 text-xs text-gray-800',
											}}
											startFrom={minDate}
											minDate={minDate}
											maxDate={maxDate}
											disabledDates={disabledPeriods}
										/>
									</div>
									<div className="w-full">
										<div className="mb-1 text-[12px] text-[#505050] font-normal">
											Horário de entrada
											<span className="text-red">*</span>
										</div>
										<input
											type="time"
											value={interval.entryTime}
											onChange={(e) =>
												onUpdateDateInterval(
													interval.id,
													'entryTime',
													e.target.value,
												)
											}
											className="w-full h-8 rounded-[4px] border border-gray-400 pl-2 text-xs text-gray-800"
										/>
									</div>
									<div className="w-full">
										<div className="mb-1 text-[12px] text-[#505050] font-normal">
											Horário de saída
											<span className="text-red">*</span>
										</div>
										<input
											type="time"
											value={interval.exitTime}
											onChange={(e) =>
												onUpdateDateInterval(
													interval.id,
													'exitTime',
													e.target.value,
												)
											}
											className="w-full h-8 rounded-[4px] border border-gray-400 pl-2 text-xs text-gray-800"
										/>
									</div>
									<div className="w-full">
										<SimpleSelect
											title="Local"
											values={locations}
											value={interval.location || ''}
											setState={(value) =>
												onUpdateDateInterval(interval.id, 'location', value)
											}
											width="100%"
											fontSize="12px"
											titleColor="text-[#505050]"
											fontWeight="font-normal"
											required={isTransportNeeded && interval.needTransport}
										/>
									</div>
									<div className="flex w-full items-end gap-1">
										<div className="min-w-0 flex-1">
											<SimpleSelect
												title="Necessidade de Transporte"
												values={yesNoOptions}
												value={
													lockNeedTransport
														? 0
														: booleanToNumber(interval.needTransport)
												}
												setState={(value) => {
													if (lockNeedTransport) {
														return;
													}
													onUpdateDateInterval(
														interval.id,
														'needTransport',
														numberToBoolean(value),
													);
												}}
												width="100%"
												fontSize="12px"
												titleColor="text-[#505050]"
												fontWeight="font-normal"
												required
												disabled={lockNeedTransport}
											/>
										</div>
										{lockNeedTransport && lockNeedTransportMessage && (
											<TooltipIconText text={lockNeedTransportMessage} />
										)}
									</div>
								</div>
								<div
									className={
										dateIntervals.length > 1
											? 'visible'
											: 'invisible pointer-events-none'
									}
								>
									<TrashButton
										onClick={() => onRemoveDateInterval(interval.id)}
									/>
								</div>
							</div>
							<div className="flex items-end gap-4">
								<div className="grid grid-cols-5 gap-4 items-end flex-1">
									<div className="w-full">
										<SimpleSelect
											title="Confinamento"
											values={yesNoOptions}
											value={booleanToNumber(interval.confinement)}
											setState={(value) =>
												onUpdateDateInterval(
													interval.id,
													'confinement',
													numberToBoolean(value),
												)
											}
											width="100%"
											fontSize="12px"
											titleColor="text-[#505050]"
											fontWeight="font-normal"
											required
										/>
									</div>
									<div className="w-full col-start-5">
										{isLastInterval ? (
											<Button
												type="button"
												variant={'elevated'}
												className="px-4 py-0 text-[0.75rem] w-fit h-7"
												onClick={onAddDateInterval}
											>
												<IoAddOutline size={16} />
												Adicionar novo intervalo
											</Button>
										) : null}
									</div>
								</div>
								<div className="w-8"></div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default DateIntervalsSection;
